import React from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import styled from 'styled-components';
import { Icon, Button, Table, Link, theme } from 'components';
import NeedContext from './NeedContext';

type AttachmentsProps = {
  canMove?: boolean;
  canDelete?: boolean;
};

const Attachments: React.FC<AttachmentsProps> = ({ canMove, canDelete }) => {
  const { files, reorderFiles, deleteFile } = React.useContext(NeedContext);

  const onSortEnd = ({ oldIndex, newIndex }: Record<string, number>) => {
    if (oldIndex !== newIndex) {
      const newFiles: typeof files = arrayMove(files, oldIndex, newIndex);
      reorderFiles(newFiles);
    }
  };

  const columns: ColumnsType<(typeof files)[number]> = [
    canMove && {
      title: '',
      dataIndex: 'id',
      key: 'sort',
      width: 32,
      render: () => <DragHandle />,
    },
    {
      title: 'Attachments',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, { url }) => (
        <Link href={url} target="_blank" rel="noreferrer">
          {name}
        </Link>
      ),
    },
    canDelete && {
      title: '',
      dataIndex: 'id',
      key: 'delete',
      width: 32,
      render: (id, file) => (
        <Button danger shape="circle" onClick={() => deleteFile(file)}>
          <Icon type="delete" color="danger" offset={[0, 2]} />
        </Button>
      ),
    },
  ];

  return (
    <StyledTable
      pagination={false}
      size="small"
      components={{
        body: {
          wrapper: (props: any) => (
            <SortableWrapper
              useDragHandle
              disableAutoscroll
              lockAxis="y"
              helperClass="row-dragging"
              onSortEnd={onSortEnd}
              {...props}
            />
          ),
          row: (props: any) => (
            <SortableItem
              index={files.findIndex(({ id }) => id === props['data-row-key'])}
              {...props}
            />
          ),
        },
      }}
      columns={columns.filter(Boolean)}
      dataSource={files}
    />
  );
};

const DragHandle = SortableHandle(() => (
  <StyledDragHandle type="barsMenu" offset={[0, 2]} />
));
const SortableWrapper = SortableContainer((props: any) => <tbody {...props} />);
const SortableItem = SortableElement((props: any) => (
  <StyledSortableItem {...props} />
));

const StyledTable = styled(Table)`
  .table-cell:hover {
    cursor: pointer;
  }
`;

const StyledSortableItem = styled.tr`
  &&.row-dragging {
    display: grid;
    grid-template-columns: 32px 1fr auto;
    align-items: center;
    padding: 0 8px 0 10px;
    background: ${theme.gray3};
  }
`;

const StyledDragHandle = styled(Icon)`
  cursor: move;
`;

export default Attachments;
